import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { PermissionService } from '@app/core/services/permission.service';
import {
  ModApprovalTimeoutConfig,
  OrderModificationApprovalService,
} from '@app/feature/center-business/order/services/order-modification-approval.service';

@Component({
  selector: 'app-modification-approval-timeout',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ToastModule,
    InputNumberModule,
    ToggleSwitchModule,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './modification-approval-timeout.component.html',
  styleUrl: './modification-approval-timeout.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModificationApprovalTimeoutComponent implements OnInit {
  private readonly api = inject(OrderModificationApprovalService);
  private readonly permissionService = inject(PermissionService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  canView = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.order.modification_approval_timeout.view',
    );
  });

  canEdit = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.order.modification_approval_timeout.edit',
    );
  });

  loading = signal(false);
  saving = signal(false);
  timeoutMinutes = signal(15);
  enabled = signal(true);
  configId = signal<number | undefined>(undefined);

  ngOnInit(): void {
    if (!this.canView()) {
      return;
    }
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.getTimeoutConfig().subscribe({
      next: (cfg: ModApprovalTimeoutConfig) => {
        this.configId.set(cfg.id);
        this.timeoutMinutes.set(cfg.timeoutMinutes ?? 15);
        this.enabled.set(cfg.enabled !== false);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.messages.error'),
          detail: this.translate.instant(
            'modification_approval_timeout.messages.load_failed',
          ),
        });
      },
    });
  }

  save(): void {
    if (!this.canEdit()) {
      return;
    }
    const minutes = this.timeoutMinutes();
    if (!minutes || minutes < 1) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.messages.warning'),
        detail: this.translate.instant(
          'modification_approval_timeout.messages.invalid_minutes',
        ),
      });
      return;
    }
    this.saving.set(true);
    this.api
      .upsertTimeoutConfig({
        id: this.configId(),
        timeoutMinutes: minutes,
        enabled: this.enabled(),
      })
      .subscribe({
        next: (cfg) => {
          this.configId.set(cfg.id);
          this.saving.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.messages.success'),
            detail: this.translate.instant(
              'modification_approval_timeout.messages.save_success',
            ),
          });
        },
        error: () => {
          this.saving.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.messages.error'),
            detail: this.translate.instant(
              'modification_approval_timeout.messages.save_failed',
            ),
          });
        },
      });
  }
}
